import { Flex } from '@chakra-ui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import { IconButton } from '@main/complykit/components/icon-button';
import { CellContext, ColumnDef, RowData } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { MenuActions } from '../../actions/menu-actions';
import type { MenuActionItem } from '../../actions/types';
import { CellContainer } from '../shared/cell';
import { actionsColumnId } from '../table-column-ids';

export interface ActionsColumnDef<TData extends RowData, TValue = unknown>
  extends Omit<ColumnDef<TData, TValue>, 'id' | 'enableHiding' | 'enableResizing'> {
  menuActions?: (context: CellContext<TData, unknown>) => MenuActionItem[];
}

declare module '@tanstack/react-table' {
  interface ColumnMeta<TData, TValue> {
    menuActions?: ActionsColumnDef<TData>['menuActions'];
  }
}

export function actions<TData>() {
  return ({ menuActions, ...columnDef }: ActionsColumnDef<TData>): ColumnDef<TData> => {
    return {
      size: 1,
      ...columnDef,
      id: actionsColumnId,
      enableResizing: false,
      enableHiding: false,

      cell: ActionsCell,

      meta: {
        ...columnDef.meta,
        name: 'Actions',
        menuActions,
      },
    };
  };
}

function ActionsCell<T extends RowData>(context: CellContext<T, unknown>) {
  const { t } = useTranslation('ui');
  const { menuActions } = context.column.columnDef.meta || {};
  const resolvedMenuActions = menuActions?.(context) || [];
  return (
    // py 2.5: the cell owns its rhythm, the 32px trigger square rides inside
    // the standard 52px row instead of inflating it.
    <CellContainer cell={context.cell} justify="end" px={3} py={2.5}>
      <Flex
        gap={1}
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <MenuActions
          actions={resolvedMenuActions}
          trigger={
            <IconButton icon={EllipsisVerticalIcon} label={t('table.column.actions.actions')} />
          }
        />
      </Flex>
    </CellContainer>
  );
}
